import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  Badge,
  TextField,
  InputAdornment,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Dashboard from '@mui/icons-material/Dashboard';
import People from '@mui/icons-material/People';
import Business from '@mui/icons-material/Business';
import Public from '@mui/icons-material/Public';
import Language from '@mui/icons-material/Language';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import AdminPanelSettings from '@mui/icons-material/AdminPanelSettings';
import History from '@mui/icons-material/History';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Logout from '@mui/icons-material/Logout';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import Search from '@mui/icons-material/Search';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import Apps from '@mui/icons-material/Apps';
import SettingsIcon from '@mui/icons-material/Settings';
import Bookmark from '@mui/icons-material/Bookmark';
import RequestIcon from '@mui/icons-material/Bookmark';
import ListAlt from '@mui/icons-material/ListAlt';
import { useNavigate, useLocation } from 'react-router-dom';
import { preloadRoute } from '../common/lazyRoute';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { priceChangeRequestService } from '../../services/priceChangeRequestService';
import { pendingSitesService } from '../../services/pendingSitesService';
import { competitorBacklinksService } from '../../services/competitorBacklinksService';
import NotificationBell from '../notifications/NotificationBell';

const drawerWidth = 220;

interface LayoutProps {
  children: React.ReactNode;
}

const PublisherLayout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin, isSuperAdmin, isPublisher, isClient } = useAuth();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchOpen, setSearchOpen] = React.useState(false);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const container = window !== undefined ? () => window.document.body : undefined;

  // Fetch pending price requests count for Super Admin
  const { data: pendingCountData } = useQuery({
    queryKey: ['pending-price-requests-count'],
    queryFn: () => priceChangeRequestService.getPendingCount(),
    enabled: isSuperAdmin,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
  
  const pendingCount = typeof pendingCountData === 'number' ? pendingCountData : 0;

  // Fetch pending sites count for Super Admin
  const { data: pendingSitesStats } = useQuery({
    queryKey: ['pending-sites-stats'],
    queryFn: () => pendingSitesService.getStats(),
    enabled: isSuperAdmin,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const pendingSitesCount = pendingSitesStats?.data?.pending || 0;

  const { data: drCalcPendingCountData } = useQuery({
    queryKey: ['dr-calculator-pending-count'],
    queryFn: () => competitorBacklinksService.getDrCalculatorPendingRequestsCount(),
    enabled: isSuperAdmin,
    refetchInterval: 30000,
  });

  const drCalcPendingCount = typeof drCalcPendingCountData === 'number' ? drCalcPendingCountData : 0;

  const { data: guestBlogArrivedCountData } = useQuery({
    queryKey: ['guest-blog-arrived-request-count'],
    queryFn: () => competitorBacklinksService.getUnseenGuestBlogArrivedCount(),
    enabled: isClient,
    refetchInterval: 45000,
  });

  const guestBlogArrivedCount = typeof guestBlogArrivedCountData === 'number' ? guestBlogArrivedCountData : 0;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    handleClose();
    logout(); // Now handles redirect internally
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/clients') return 'Clients';
    if (path === '/projects') return 'Projects';
    if (path.includes('/historical-usage')) return 'Historical Usage Upload';
    if (path === '/planning') return 'Planning';
    if (path === '/publishers') return 'Publishers';
    if (path === '/sites') return 'Guest Blog Sites';
    if (path === '/orders') return 'Orders';
    if (path === '/guest-blog-placements') return 'Placements';
    if (path === '/live-link-status') return 'Backlink Health';
    if (path === '/admin-billing' || path === '/publisher-billing') return 'Billing';
    if (path === '/invoices') return 'Invoices';
    if (path === '/pending-sites') return 'Pending Sites';
    if (path === '/price-change-requests') return 'Price Requests';
    if (path === '/users') return 'Users';
    if (path === '/audit') return 'Audit Logs';
    if (path === '/competitor-backlink-site-requests') return 'Competitor Backlink Site Requests';
    if (path === '/competitor-backlink-analysis') return 'Competitor Backlink Analysis';
    if (path === '/requested-sites') return 'Requested sites';
    return 'Dashboard';
  };

  // Build menu items based on role with proper sections matching reference
  const overviewItems = isPublisher ? [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  ] : isClient ? [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Projects', icon: <Business />, path: '/projects' },
    { text: 'Planning', icon: <FilterListIcon />, path: '/planning' },
  ] : [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Clients', icon: <People />, path: '/clients' },
    { text: 'Projects', icon: <Business />, path: '/projects' },
    { text: 'Planning', icon: <FilterListIcon />, path: '/planning' },
  ];

  const contentItems = isPublisher ? [
    { text: 'Guest Blog Sites', icon: <Language />, path: '/sites' },
    { text: 'Placements', icon: <PlaceOutlined />, path: '/guest-blog-placements' },
  ] : isClient ? [
    { text: 'Guest Blog Sites', icon: <Language />, path: '/sites' },
    {
      text: 'Requested sites',
      icon: <ListAlt />,
      path: '/requested-sites',
      ...(guestBlogArrivedCount > 0 && { badge: guestBlogArrivedCount }),
    },
    { text: 'Placements', icon: <PlaceOutlined />, path: '/guest-blog-placements' },
  ] : [
    ...(isSuperAdmin ? [{ text: 'Publishers', icon: <Public />, path: '/publishers' }] : []),
    { text: 'Guest Blog Sites', icon: <Language />, path: '/sites' },
    { text: 'Orders', icon: <ShoppingCart />, path: '/orders' },
    { text: 'Placements', icon: <PlaceOutlined />, path: '/guest-blog-placements' },
  ];

  const monitoringItems = isPublisher ? [
    { text: 'Backlink Health', icon: <MonitorHeartIcon />, path: '/live-link-status' },
    { text: 'Billing', icon: <ReceiptIcon />, path: '/publisher-billing' },
  ] : isClient ? [
    { text: 'Backlink Health', icon: <MonitorHeartIcon />, path: '/live-link-status' },
  ] : isSuperAdmin ? [
    { text: 'Backlink Health', icon: <MonitorHeartIcon />, path: '/live-link-status' },
    { text: 'Billing', icon: <ReceiptIcon />, path: '/admin-billing' },
  ] : [
    { text: 'Backlink Health', icon: <MonitorHeartIcon />, path: '/live-link-status' },
  ];


    const superAdminItems = [
    { text: 'Pending Sites', icon: <PendingActionsIcon />, path: '/pending-sites', ...(pendingSitesCount > 0 && { badge: pendingSitesCount }) },
    { text: 'Price Requests', icon: <AttachMoneyIcon />, path: '/price-change-requests', ...(pendingCount > 0 && { badge: pendingCount }) },
    {
      text: 'Competitor Backlink Site Requests',
      icon: <RequestIcon />,
      path: '/competitor-backlink-site-requests',
      ...(drCalcPendingCount > 0 && { badge: drCalcPendingCount }),
    },
    { text: 'Users', icon: <AdminPanelSettings />, path: '/users' },
    { text: 'Audit Logs', icon: <History />, path: '/audit' },
    { text: 'Invoices', icon: <ReceiptIcon />, path: '/invoices' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  const drawer = (
    <div style={{ position: 'relative', height: '100%' }}>
      {/* Sidebar Logo */}
      <div style={{
        padding: '20px 20px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <img 
          src="/logo.png"
          alt="Hypwave"
          style={{
            width: '32px',
            height: '32px',
            objectFit: 'contain',
            flexShrink: 0
          }}
        />
        <span style={{
          fontSize: '15px',
          fontWeight: 600,
          color: 'var(--text-primary)',
          letterSpacing: '-0.3px'
        }}>Link <span style={{ color: 'var(--accent)' }}>Management</span></span>
      </div>
      {/* Sidebar Navigation */}
      <nav style={{
        padding: '12px 10px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '1px'
      }}>
        {/* Overview Section */}
        <div style={{
          fontSize: '10px',
          fontWeight: 600,
          color: 'var(--text-muted)',
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
          padding: '12px 10px 6px'
        }}>Overview</div>
        
        {overviewItems.map((item) => (
          <a
            key={item.path}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              preloadRoute(item.path);
              navigate(item.path);
            }}
            className={location.pathname === item.path ? 'active' : ''}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '8px 10px',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              color: location.pathname === item.path ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: '13.5px',
              fontWeight: location.pathname === item.path ? 500 : 450,
              textDecoration: 'none',
              position: 'relative',
              background: location.pathname === item.path ? 'var(--accent-soft)' : 'transparent'
            }}
            onMouseEnter={(e) => {
              preloadRoute(item.path);
              if (location.pathname !== item.path) {
                e.currentTarget.style.background = 'var(--bg)';
                e.currentTarget.style.color = 'var(--text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (location.pathname !== item.path) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }
            }}
          >
            <div style={{
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: location.pathname === item.path ? 1 : 0.7,
              flexShrink: 0,
              fontSize: '16px'
            }}>
              {React.cloneElement(item.icon as React.ReactElement, { style: { fontSize: '16px' } })}
            </div>
            {item.text}
          </a>
        ))}

        {/* Content Section */}
        {contentItems.length > 0 && (
          <>
            <div style={{
              fontSize: '10px',
              fontWeight: 600,
              color: 'var(--text-muted)',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              padding: '12px 10px 6px'
            }}>Content</div>
            
            {contentItems.map((item) => (
              <a
                key={item.path}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  preloadRoute(item.path);
                  navigate(item.path);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  color: location.pathname.startsWith(item.path) ? 'var(--accent)' : 'var(--text-secondary)',
                  fontSize: '13.5px',
                  fontWeight: location.pathname.startsWith(item.path) ? 500 : 450,
                  textDecoration: 'none',
                  position: 'relative',
                  background: location.pathname.startsWith(item.path) ? 'var(--accent-soft)' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  preloadRoute(item.path);
                  if (!location.pathname.startsWith(item.path)) {
                    e.currentTarget.style.background = 'var(--bg)';
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!location.pathname.startsWith(item.path)) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }
                }}
              >
                <div style={{
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: location.pathname.startsWith(item.path) ? 1 : 0.7,
                  flexShrink: 0,
                  fontSize: '16px'
                }}>
                  {React.cloneElement(item.icon as React.ReactElement, { style: { fontSize: '16px' } })}
                </div>
                {item.text}
                {'badge' in item && typeof (item as { badge?: number }).badge === 'number' && (item as { badge: number }).badge > 0 && (
                  <span
                    style={{
                      marginLeft: 'auto',
                      background: 'var(--red)',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 600,
                      padding: '1px 6px',
                      borderRadius: '99px',
                      fontFamily: 'DM Mono, monospace',
                    }}
                  >
                    {(item as { badge: number }).badge}
                  </span>
                )}
              </a>
            ))}
          </>
        )}

        {/* Monitoring Section */}
        {monitoringItems.length > 0 && (
          <>
            <div style={{
              fontSize: '10px',
              fontWeight: 600,
              color: 'var(--text-muted)',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              padding: '12px 10px 6px'
            }}>Monitoring</div>
            
            {monitoringItems.map((item) => (
              <a
                key={item.path}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  preloadRoute(item.path);
                  navigate(item.path);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  color: location.pathname.startsWith(item.path) ? 'var(--accent)' : 'var(--text-secondary)',
                  fontSize: '13.5px',
                  fontWeight: location.pathname.startsWith(item.path) ? 500 : 450,
                  textDecoration: 'none',
                  position: 'relative',
                  background: location.pathname.startsWith(item.path) ? 'var(--accent-soft)' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  preloadRoute(item.path);
                  if (!location.pathname.startsWith(item.path)) {
                    e.currentTarget.style.background = 'var(--bg)';
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!location.pathname.startsWith(item.path)) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }
                }}
              >
                <div style={{
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: location.pathname.startsWith(item.path) ? 1 : 0.7,
                  flexShrink: 0,
                  fontSize: '16px'
                }}>
                  {React.cloneElement(item.icon as React.ReactElement, { style: { fontSize: '16px' } })}
                </div>
                {item.text}
              </a>
            ))}
          </>
        )}

        {/* Admin Section */}
        {isSuperAdmin && (
          <>
            <div style={{
              fontSize: '10px',
              fontWeight: 600,
              color: 'var(--text-muted)',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              padding: '12px 10px 6px'
            }}>Admin</div>
            
            {superAdminItems.map((item) => (
              <a
                key={item.path}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  preloadRoute(item.path);
                  navigate(item.path);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  color: location.pathname.startsWith(item.path) ? 'var(--accent)' : 'var(--text-secondary)',
                  fontSize: '13.5px',
                  fontWeight: location.pathname.startsWith(item.path) ? 500 : 450,
                  textDecoration: 'none',
                  position: 'relative',
                  background: location.pathname.startsWith(item.path) ? 'var(--accent-soft)' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  preloadRoute(item.path);
                  if (!location.pathname.startsWith(item.path)) {
                    e.currentTarget.style.background = 'var(--bg)';
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!location.pathname.startsWith(item.path)) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }
                }}
              >
                <div style={{
                  width: '16px',
                  height: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: location.pathname.startsWith(item.path) ? 1 : 0.7,
                  flexShrink: 0,
                  fontSize: '16px'
                }}>
                  {React.cloneElement(item.icon as React.ReactElement, { style: { fontSize: '16px' } })}
                </div>
                {item.text}
                {item.badge && item.badge > 0 && (
                  <span style={{
                    marginLeft: 'auto',
                    background: 'var(--red)',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: 600,
                    padding: '1px 6px',
                    borderRadius: '99px',
                    fontFamily: 'DM Mono, monospace'
                  }}>
                    {item.badge}
                  </span>
                )}
              </a>
            ))}
          </>
        )}
      </nav>
      
      {/* Sidebar Footer */}
      <div style={{
        padding: '12px 10px',
        borderTop: '1px solid var(--border-light)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '8px 10px',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          transition: 'background 0.15s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
        >
          <div style={{
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 600,
            color: 'white',
            flexShrink: 0
          }}>
            {user?.firstName?.charAt(0).toUpperCase()}{user?.lastName?.charAt(0).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 500,
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {user?.firstName} {user?.lastName}
            </div>
            <div style={{
              fontSize: '11px',
              color: 'var(--text-muted)'
            }}>
              {isSuperAdmin ? 'Super Admin' : isPublisher ? 'Publisher' : isClient ? 'Client' : 'Admin'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
          color: 'var(--text-primary)',
          boxShadow: 'none',
          height: 'var(--header-h)',
          borderRadius: 0,
        }}
      >
        <Toolbar sx={{ 
          px: 3, 
          py: 0, 
          justifyContent: 'space-between', 
          minHeight: 'var(--header-h) !important',
          height: 'var(--header-h)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: 'var(--text-primary)',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              {getPageTitle()}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <NotificationBell />
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              transition: 'all 0.3s ease',
              width: searchOpen ? '300px' : 'auto'
            }}>
              {searchOpen && (
                <input
                  type="text"
                  placeholder="Search..."
                  autoFocus
                  onBlur={() => setSearchOpen(false)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                    backgroundColor: 'var(--surface)',
                    color: 'var(--text-primary)',
                    marginRight: '8px',
                    transition: 'all 0.3s ease'
                  }}
                />
              )}
              <Tooltip title="Search">
                <IconButton 
                  onClick={() => setSearchOpen(!searchOpen)}
                  sx={{ 
                    color: 'var(--text-muted)',
                    width: 36,
                    height: 36,
                    '&:hover': {
                      backgroundColor: 'var(--bg)',
                      color: 'var(--text-primary)'
                    }
                  }}>
                  <SearchOutlined sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            
            <Tooltip title="Account">
              <IconButton
                onClick={handleMenu}
                sx={{ p: 0.5 }}
              >
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32, 
                    background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                    fontSize: '12px', 
                    fontWeight: 600 
                  }}
                >
                  AH
                </Avatar>
              </IconButton>
            </Tooltip>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              onClick={handleClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  backgroundColor: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-sm)',
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'var(--surface)',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    border: '1px solid var(--border)',
                    borderRight: 'none',
                    borderBottom: 'none'
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem 
                onClick={handleClose}
                sx={{
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  cursor: 'default',
                  '&:hover': {
                    backgroundColor: 'transparent'
                  }
                }}
              >
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {user?.firstName} {user?.lastName}
                  <Typography variant="body2" color="text.secondary">
                    {user?.email}
                  </Typography>
                </ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem 
                onClick={handleLogout}
                sx={{
                  fontSize: '14px',
                  color: 'var(--text-primary)',
                  '&:hover': {
                    backgroundColor: 'var(--bg)'
                  }
                }}
              >
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: 'var(--surface)',
              borderRight: '1px solid var(--border)',
              boxShadow: 'var(--shadow-lg)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: 'var(--surface)',
              borderRight: '1px solid var(--border)',
              boxShadow: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 0,
          py: 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'var(--bg)',
        }}
      >
        <Toolbar sx={{ minHeight: 'var(--header-h) !important' }} />
        <Box
          sx={{
            px: { xs: 2, sm: 3.25 },
            pt: { xs: 2, sm: 2.75 },
            pb: 4,
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default PublisherLayout;
